<script setup lang="ts">
import { useManufacturersStore } from "~/stores/manufacturers";

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const manufacturersStore = useManufacturersStore();
const { bucketBaseUrl } = useHelpers();
const { t } = useI18n();

useHead({
  htmlAttrs: {
    lang: config.public.meta.lang,
  },
  title: `${t("manufacturers_page.title")} | Mountex`,
  meta: [
    {
      name: "description",
      content: t("manufacturers_page.description"),
    },
  ],
});

// -----------------------
// reactive properties
// -----------------------
const index = computed(() => {
  return manufacturersStore.manufacturers
    .map((manufacturer) => manufacturer.name.toUpperCase()[0])
    .filter((value, index, self) => self.indexOf(value) === index);
});

// -----------------------
// helper methods
// -----------------------
const getManufacturersWithLetter = (letter: string) => {
  return manufacturersStore.manufacturers.filter(
    (manufacturer) =>
      manufacturer.name.toUpperCase()[0] === letter.toUpperCase(),
  );
};

const scrollToContainer = (e: Event, letter: string) => {
  e.preventDefault();
  const element = document.querySelector("#" + letter);

  if (element) {
    const offset = 80; // Scroll 80px before the element
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY; // Absolute position
    window.scrollTo({
      top: elementPosition - offset,
      behavior: "smooth",
    });
  }
};

const { width } = useWindowSize(); // Reactive window width

const backgroundImage = computed(() => {
  return width.value < 768
    ? `${bucketBaseUrl()}Public/assets/manufacturers_header_mobile.png`
    : `${bucketBaseUrl()}Public/assets/manufacturers_header.png`;
});
</script>

<template>
  <div>
    <header
      class="flex justify-center items-center h-[150px] md:h-[300px] bg-center bg-cover mb-[32px] md:mb-[80px]"
      :style="`
        background-image: url('${backgroundImage}');
      `"
    >
      <h1 class="!text-3xl md:!text-4xl text-brand-surface font-bold !mt-0">
        {{ t("our_brands") }}
      </h1>
    </header>
    <div class="relative mx-[12px] md:mx-0">
      <!-- Index -->
      <div
        class="flex flex-wrap items-center justify-center text-brand-headline font-bold text-sm md:text-xl mb-[32px] md:mb-[80px] px-[7%] md:px-[18%]"
      >
        <div v-for="(letter, i) in index" :key="letter">
          <a
            :href="'#' + letter"
            class="unstyled"
            @click="scrollToContainer($event, letter)"
            >{{ letter }}</a
          >
          <span v-if="i !== index.length - 1" class="mx-12"> • </span>
        </div>
      </div>

      <!-- Lists -->
      <div class="px-0 md:px-[12%]">
        <div v-for="letter in index" :id="letter" :key="letter">
          <div
            class="h-[50px] rounded-[16px] bg-brand-surface text-xl text-brand-headline font-bold px-[20px] py-[11px]"
          >
            {{ letter }}
          </div>
          <div class="flex flex-wrap items-center p-[24px]">
            <a
              v-for="manufacturer in getManufacturersWithLetter(letter)"
              :key="manufacturer.slug"
              class="unstyled text-brand-body text-base leading-[30px] mr-[40px]"
              :href="`/${config.public.routes.manufacturers}/${manufacturer.slug}`"
              >{{ manufacturer.name }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
